/* In-memory mocked client for Cash Management — no real backend exists for this domain yet. */

import {
  CashMovementDto,
  CashMovementType,
  CashBalanceDto,
  TillReconciliationDto,
  CashSummaryDto,
  CreateCashMovementRequest,
  CreateTillReconciliationRequest,
} from './cashManagementTypes';
import { ICashManagementClient } from './ICashManagementClient';

const movementTypes: CashMovementType[] = ['Sale', 'FloatIn', 'FloatOut', 'CashIn', 'CashOut', 'BankDeposit', 'Petty Cash'];

function randomMovement(id: number, storeNumber: string, daysAgo: number): CashMovementDto {
  const type = movementTypes[Math.floor(Math.random() * movementTypes.length)];
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(Math.floor(Math.random() * 14) + 6);
  const timestamp = date.toISOString();
  return {
    id,
    storeNumber,
    amount: parseFloat((Math.random() * 500 + 10).toFixed(2)),
    currencyCode: 'GBP',
    movementType: type,
    timestamp,
    userId: `user-${(id % 3) + 1}`,
    username: ['John Smith', 'Jane Doe', 'Bob Wilson'][id % 3],
    reference: `REF-${id.toString().padStart(4, '0')}`,
    notes: '',
    createdAtUtc: timestamp,
  };
}

const mockMovements: CashMovementDto[] = Array.from({ length: 30 }, (_, i) => randomMovement(i + 1, 'STORE001', i % 7));

const mockReconciliations: TillReconciliationDto[] = [
  {
    id: 1,
    storeNumber: 'STORE001',
    tillNumber: 'TILL-01',
    reconciliationDate: new Date().toISOString(),
    openingBalance: 500,
    closingBalance: 1245.5,
    expectedClosingBalance: 1250,
    variance: -4.5,
    currencyCode: 'GBP',
    reconciledBy: 'John Smith',
    notes: '',
    status: 'Reconciled',
    createdAtUtc: new Date().toISOString(),
  },
  {
    id: 2,
    storeNumber: 'STORE001',
    tillNumber: 'TILL-02',
    reconciliationDate: new Date(Date.now() - 86400000).toISOString(),
    openingBalance: 500,
    closingBalance: 980,
    expectedClosingBalance: 980,
    variance: 0,
    currencyCode: 'GBP',
    reconciledBy: 'Jane Doe',
    notes: '',
    status: 'Reconciled',
    createdAtUtc: new Date().toISOString(),
  },
];

let nextMoveId = 100;
let nextReconId = 100;

export class CashManagementClientMocked implements ICashManagementClient {
  async getCashMovements(storeNumber: string, startDate: string, endDate: string): Promise<CashMovementDto[]> {
    // Demo data ignores store scoping entirely - return movements regardless of store.
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return mockMovements.filter((m) => {
      const t = new Date(m.timestamp).getTime();
      return t >= start && t <= end;
    });
  }

  async getCashBalance(storeNumber: string): Promise<CashBalanceDto> {
    return {
      storeNumber,
      currentBalance: 1245.5,
      expectedBalance: 1250,
      variance: -4.5,
      currencyCode: 'GBP',
      lastUpdatedUtc: new Date().toISOString(),
      tillBalances: [
        { tillNumber: 'TILL-01', balance: 645.5, status: 'Open' },
        { tillNumber: 'TILL-02', balance: 600, status: 'Reconciled', lastReconciliationUtc: new Date(Date.now() - 86400000).toISOString() },
      ],
    };
  }

  async recordCashMovement(movement: CreateCashMovementRequest): Promise<CashMovementDto> {
    const timestamp = new Date().toISOString();
    const created: CashMovementDto = {
      id: nextMoveId++,
      storeNumber: movement.storeNumber,
      tillNumber: movement.tillNumber,
      movementType: movement.movementType,
      amount: movement.amount,
      currencyCode: 'GBP',
      timestamp,
      userId: 'user-1',
      username: 'Demo User',
      reference: movement.reference,
      notes: movement.notes,
      transactionId: movement.transactionId,
      createdAtUtc: timestamp,
    };
    mockMovements.unshift(created);
    return created;
  }

  async getTillReconciliations(storeNumber: string, startDate: string, endDate: string): Promise<TillReconciliationDto[]> {
    // Demo data ignores store scoping entirely - return reconciliations regardless of store.
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return mockReconciliations.filter((r) => {
      const t = new Date(r.reconciliationDate).getTime();
      return t >= start && t <= end;
    });
  }

  async createTillReconciliation(reconciliation: CreateTillReconciliationRequest): Promise<TillReconciliationDto> {
    const created: TillReconciliationDto = {
      id: nextReconId++,
      storeNumber: reconciliation.storeNumber,
      tillNumber: reconciliation.tillNumber,
      reconciliationDate: new Date().toISOString(),
      openingBalance: 0,
      closingBalance: reconciliation.closingBalance,
      expectedClosingBalance: reconciliation.closingBalance,
      variance: 0,
      currencyCode: 'GBP',
      reconciledBy: 'Demo User',
      notes: reconciliation.notes,
      status: 'Reconciled',
      createdAtUtc: new Date().toISOString(),
      cashBreakdown: reconciliation.cashBreakdown,
    };
    mockReconciliations.unshift(created);
    return created;
  }

  async getCashSummary(storeNumber: string, date: string): Promise<CashSummaryDto> {
    return {
      storeNumber,
      date,
      totalSales: 4520.75,
      totalCashSales: 1230.5,
      totalCardSales: 3290.25,
      totalRefunds: 45.0,
      totalCashIn: 200,
      totalCashOut: 100,
      bankDeposits: 500,
      openingBalance: 500,
      closingBalance: 1245.5,
      expectedBalance: 1250,
      variance: -4.5,
      currencyCode: 'GBP',
      paymentMethodBreakdown: { Cash: 1230.5, Card: 2100.25, Contactless: 1190 },
    };
  }
}
